import React, { useEffect, useMemo, useRef, useState } from 'react';
import { urlFor } from '../../lib/sanityImage';
import { FACTOID_INTERVAL_MS } from '../../config/timing';
// Icon not needed for CTA currently

// Simple in-place shuffle (Fisher–Yates)
function shuffle(array) {
  const a = [...array];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Map related Sanity types to on-site routes
function buildRelatedUrl(related) {
  if (!related || !related._type || !related.slug) return null;
  const slug = typeof related.slug === 'string' ? related.slug : related.slug?.current;
  if (!slug) return null;

  switch (related._type) {
    case 'blogPost':
      return `/blog/${slug}`;
    case 'restaurant':
      return `/restaurants/${slug}`;
    case 'lodging':
      return `/lodging/${slug}`;
    case 'activity':
      return `/activities/${slug}`;
    case 'region':
      return `/regions/${slug}`;
    case 'area':
      return `/areas/${slug}`;
    default:
      return null;
  }
}

/**
 * FactoidRotator
 * Props:
 * - factoids?: array of { title, expandedText, image?, relatedContent? }
 * - intervalMs?: number (default FACTOID_INTERVAL_MS) - duration each factoid displays
 * - limit?: number (default 20) - max factoids to show
 * - className?: string
 * - showHint?: boolean (default true)
 * - hintText?: string - optional copy above the card
 * - onExploreClick?: (factoid) => void
 * - onCycleEnd?: (index) => void - called when exitArmed and current factoid completes
 * - exitArmed?: boolean (default false) - when true, will exit after current factoid finishes
 * - onFactoidChange?: (index) => void - called when the factoid changes to a new one
 * - prominentHint?: boolean (default false) - when true, makes hint text larger and more visible
 */
export default function FactoidRotator({
  factoids = [],
  intervalMs = FACTOID_INTERVAL_MS,
  limit = 20,
  className = '',
  showHint = true,
  hintText,
  onExploreClick,
  onCycleEnd,
  exitArmed = false,
  onFactoidChange,
  prominentHint = false,
}) {
  const reducedMotion = useMemo(() =>
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  []);

  const items = useMemo(() => shuffle(factoids).slice(0, limit), [factoids, limit]);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const [paused, setPaused] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);
  const currentFactoidStartTimeRef = useRef(Date.now());
  const onCycleEndRef = useRef(onCycleEnd);
  const exitArmedRef = useRef(exitArmed);
  const shouldExitAfterCurrentFactoidRef = useRef(false);
  

  // Keep latest callback without re-wiring the effect
  useEffect(() => {
    onCycleEndRef.current = onCycleEnd;
  }, [onCycleEnd]);

  // Track latest exitArmed value and set flag to exit after current factoid completes
  useEffect(() => {
    exitArmedRef.current = exitArmed;
    if (exitArmed && !shouldExitAfterCurrentFactoidRef.current) {
      shouldExitAfterCurrentFactoidRef.current = true;
      // If no timer is running (e.g., only 1 factoid), exit immediately with transition
      if (!timerRef.current && items.length > 0) {
        setIsExiting(true);
        setTimeout(() => {
          if (typeof onCycleEndRef.current === 'function') {
            onCycleEndRef.current(indexRef.current);
          }
        }, 300);
      }
    }
  }, [exitArmed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Advance index on interval
  // Keep a ref in sync with the current index
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  // Call onFactoidChange when index changes (separate effect to avoid issues)
  useEffect(() => {
    if (onFactoidChange && index > 0) {
      onFactoidChange(index);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]); // Intentionally not including onFactoidChange to avoid re-triggers

  useEffect(() => {
    
    if (items.length <= 1) return; // nothing to rotate
    if (reducedMotion) return; // respect reduced motion
    if (paused) return;
    if (isExiting) return; // don't start new intervals when exiting

    // Record when this factoid started displaying
    const startTime = Date.now();
    currentFactoidStartTimeRef.current = startTime;
    

    timerRef.current = setInterval(() => {
      const currentIndex = indexRef.current;
      
      // If we're armed to exit, exit after this natural 6-second boundary
      if (shouldExitAfterCurrentFactoidRef.current) {
        setIsExiting(true);
        clearInterval(timerRef.current);
        timerRef.current = null;
        
        // Start fade transition, then call onCycleEnd after 300ms
        setTimeout(() => {
          if (typeof onCycleEndRef.current === 'function') {
            onCycleEndRef.current(currentIndex);
          }
        }, 300);
        return;
      }
      
      // Normal advance to next factoid
      setIndex((i) => (i + 1) % items.length);
      currentFactoidStartTimeRef.current = Date.now(); // Record start time for next factoid
    }, intervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [items.length, intervalMs, paused, reducedMotion, isExiting]);
  // We intentionally do not notify on normal cycle boundaries; parent cares only about exit.

  const current = items[index];


  // Handlers to pause/resume only for the "Read more" button
  const handleButtonMouseEnter = () => setPaused(true);
  const handleButtonMouseLeave = () => setPaused(false);
  const handleButtonFocus = () => setPaused(true);
  const handleButtonBlur = () => setPaused(false);

  if (!items || items.length === 0) {
    return (
      <div className={`w-full max-w-md mx-auto p-4 ${className}`}>
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/60 mr-3"></div>
          <p className="text-white/70">Finding a fun fact...</p>
        </div>
      </div>
    );
  }

  const relatedUrl = buildRelatedUrl(current?.relatedContent);
  // Request a 6:5 server-side crop so the CDN honors the image hotspot
  const imageUrl = current?.image
    ? urlFor(current.image).width(720).height(600).fit('crop').auto('format').url()
    : null;

  return (
    <div className={`w-full mx-auto ${className}`}>
      {showHint && (
        <div className={`text-center mb-8 ${prominentHint ? '' : 'text-xs text-white/60'}`}>
          {prominentHint ? (
            <div className="inline-block bg-white/5 border border-emerald-400/30 rounded-lg px-6 py-3">
              <span className="text-base md:text-lg text-emerald-300 font-medium">
                {hintText || 'A Hawaii fact while we get things ready'}
              </span>
            </div>
          ) : (
            <span>{hintText || 'A Hawaii fact while we get things ready'}</span>
          )}
        </div>
      )}

      <div
        className={`relative glass-card rounded-lg overflow-hidden border border-white/10 w-[300px] md:w-[360px] mx-auto transition-opacity duration-300 ${reducedMotion ? '' : 'glass-pulse'} ${paused ? 'factoid-anim-paused' : ''} ${isExiting ? 'opacity-0' : 'opacity-100'}`}
      >
        <div className="p-4">
          <div aria-live="polite" aria-atomic="true">
            {current?.title && (
              <h3 className="text-lg font-semibold text-white mb-2 text-center">{current.title}</h3>
            )}
          </div>

          {imageUrl && (
            <div className="w-full rounded-md overflow-hidden mb-3" style={{ aspectRatio: '6 / 5' }}>
              <img
                src={imageUrl}
                alt={current?.image?.alt || current?.title || 'Factoid image'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}

          <div aria-live="polite" aria-atomic="true">
            {current?.expandedText && (
              <p className="text-white leading-relaxed text-sm text-center">{current.expandedText}</p>
            )}
          </div>

          {relatedUrl && (
            <div className="mt-3 text-center">
              <a
                href={relatedUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onExploreClick && onExploreClick(current)}
                onMouseEnter={handleButtonMouseEnter}
                onMouseLeave={handleButtonMouseLeave}
                onFocus={handleButtonFocus}
                onBlur={handleButtonBlur}
                className="inline-flex items-center text-white hover:text-slate-200 underline underline-offset-4"
              >
                Read more
              </a>
            </div>
          )}
        </div>

        {/* Perimeter progress indicator (draws around the card) */}
        {!reducedMotion && (
          <svg
            key={index}
            className="pointer-events-none absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <rect
              x="1" y="1" width="98" height="98" rx="2.5" ry="2.5"
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              pathLength="100"
              className="factoid-border-progress"
              style={{ animationDuration: `${intervalMs}ms`, strokeDasharray: '0 100' }}
            />
          </svg>
        )}
      </div>
    </div>
  );
}
